import React, { FC, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation, WaitFor } from '../components/blocks';
import { usePageMessaging } from '../components/composites';
import ClaimDetails from '../components/composites/ClaimDetails';
import { ClaimSummary } from '../components/composites/ClaimSummary';
import { getDetailedClaim as fetchDetailedClaim } from '../redux/actions/detailedClaimActionCreators';
import { getResellerItems } from '../redux/actions/resellerActionCreators';
import { ClaimWithMetadata, RootState } from '../redux/types';
import { Logger } from '../utils/logger';

const ClaimViewPage: FC<ClaimViewPageProps> = ({
    resellerItems,
    resellerId,
    detailedClaim,
    getResellerItems,
    fetchDetailedClaim,
    user,
}) => {
    const { t } = useTranslation(['claimDetails', 'selectResellerPage']);
    const pageMessaging = usePageMessaging();
    const [hasErrorBeenReportedResellerItems, setHasErrorBeenReportedResellerItems] = useState<boolean>(false);
    const { resellerClaimNumber } = useParams<{ resellerClaimNumber: string }>();
    const decodedResellerClaimNumber = decodeURIComponent(resellerClaimNumber);

    useEffect(() => {
        if (resellerId) {
            switch (resellerItems.status) {
                case 'Uninitialized':
                    getResellerItems(resellerId);
                    setHasErrorBeenReportedResellerItems(false);
                    break;
                case 'Failed':
                    Logger.error('getResellerItems error: ', resellerItems.error);
                    if (!hasErrorBeenReportedResellerItems) {
                        pageMessaging.showError(t('selectResellerPage:failedToLoadResellerItems-errorMessage'));
                        setHasErrorBeenReportedResellerItems(true);
                    }
                    break;
                default:
                // nothing
            }
        }
    }, [resellerItems, resellerId, getResellerItems, pageMessaging, t, hasErrorBeenReportedResellerItems]);

    useEffect(() => {
        if (decodedResellerClaimNumber && resellerId) {
            fetchDetailedClaim(resellerId, decodedResellerClaimNumber);
        }
    }, [decodedResellerClaimNumber, fetchDetailedClaim, resellerId]);

    return (
        <WaitFor lazyValue={detailedClaim}>
            {(claimWithMetadata: ClaimWithMetadata) => {
                const hasClaimId = claimWithMetadata.claimMetadata?.claimId;

                if (hasClaimId) {
                    return (
                        <WaitFor lazyValue={resellerItems}>
                            {(loadedItems) => (
                                <ClaimDetails
                                    resellerId={resellerId!}
                                    claimWithMetadata={claimWithMetadata}
                                    resellerItems={loadedItems}
                                    username={user?.username || ''}
                                    fileId={claimWithMetadata.claimMetadata?.fileId}
                                />
                            )}
                        </WaitFor>
                    );
                } else {
                    return <ClaimSummary claimData={claimWithMetadata.claimMetadata} />;
                }
            }}
        </WaitFor>
    );
};

const mapStateToProps = ({ resellerReducer, detailedClaimReducer, userReducer }: RootState) => ({
    user: userReducer.user,
    resellerItems: resellerReducer.resellerItems,
    resellerId: resellerReducer.selectedReseller.value?.resellerId,
    detailedClaim: detailedClaimReducer.detailedClaim,
});

const mapDispatchToProps = {
    getResellerItems,
    fetchDetailedClaim,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type ClaimViewPageProps = ConnectedProps<typeof connector>;
export default connector(ClaimViewPage);
